/**
 * render When user is authenticate
 * Dashboard component that displays and manages user bots,
 * handles training workflows, and renders UI based on selection.
 * @component
 */
import React, { useEffect, useReducer, useRef, useState } from "react";
import {
  Navbar,
  BotList,
  EmptyList,
  SelectedBot,
  AccoundSettings,
} from "./component";
import "./styles/index.css";
import { useGetUserQuery } from "../../features/auth/userApi";
import { setUser, setError, setLoading } from "../../features/auth/userSlice";
import { useDispatch, useSelector } from "react-redux";
import {
  GetCrawlLink,
  GetListOfPendingTask,
  GetUserBots,
  TrainBotOnData,
  UploadPdf,
  UploadText,
} from "../../apis";
import CreateNewBot from "../../apis/CreateNewBot";
import { toast } from "react-toastify";
import DemoChat from "./component/Sandbox/DemoChat";
import Usages from "./component/Usages";

const initialState = {
  selectedBot: null,
  bots: [],
  isLoading: false,
  error: null,
  isEmpty: true,
  isSelected: false,
  activeAccountMenus: "dashboard",
  activeBotMenus: "sandbox",
  showDemoChat: false,
  task: {},
  // urls: [],
  // selectedUrls: [],
  // title: "",
  // text: "",
  // pdfUrls: [],
  // pdfFiles: [],
  // usedDocs: [],
  botResources: {},
};

/**
 * Reducer function to manage Dashboard local state.
 * @param {Object} state - Current state of the dashboard.
 * @param {Object} action - Action object to update the state.
 * @param {string} action.type - Type of action.
 * @param {any} [action.payload] - Optional payload to update state.
 * @returns {Object} Updated state object.
 */

const reducer = (state, action) => {
  switch (action.type) {
    case "SET_SELECTED_BOT":
      return {
        ...state,
        selectedBot: action.payload,
        isSelected: !!action.payload,
      };
    case "SET_BOTS":
      return {
        ...state,
        bots: action.payload,
        isEmpty: action.payload?.length === 0,
      };
    case "SET_LOADING":
      return { ...state, isLoading: action.payload };
    case "SET_ERROR":
      return { ...state, error: action.payload };
    case "SET_EMPTY":
      return { ...state, isEmpty: action.payload };
    case "SET_SELECTED":
      return { ...state, isSelected: action.payload };
    case "SET_ACTIVE_MENU":
      return { ...state, activeAccountMenus: action.payload };
    case "SET_ACTIVE_BOT_MENU":
      return { ...state, activeBotMenus: action.payload };
    case "Demo_Chat":
      return { ...state, showDemoChat: !state.showDemoChat };
    case "M_Demo_Chat":
      return { ...state, showDemoChat: action.payload };
    case "setTask":
      return { ...state, task: action.payload || {} };
    // case "setUrls":
    //   return { ...state, urls: [...state.urls, action.payload] };
    // case "ModifyUrls":
    //   return { ...state, urls: action.payload || [] };
    // case "setText":
    //   return { ...state, text: action.payload };
    // case "setTitle":
    //   return { ...state, title: action.payload };
    // case "setPdfurls":
    //   return { ...state, pdfUrls: action.payload || [] };
    // case "setPdfFiles":
    //   return { ...state, pdfFiles: action.payload || [] };
    // case "setSelectedUrls":
    //   return { ...state, selectedUrls: action.payload || [] };
    // case "setUsedDocs":
    //   return { ...state, usedDocs: action.payload || [] };

    case "UPDATE_BOT_RESOURCE": {
      const { botId, data } = action.payload;
      return {
        ...state,
        botResources: {
          ...state.botResources,
          [botId]: {
            ...state.botResources[botId],
            ...data,
          },
        },
      };
    }

    case "RESET_BOT_RESOURCE": {
      const { botId } = action.payload;
      return {
        ...state,
        botResources: {
          ...state.botResources,
          [botId]: {
            urls: [],
            selectedUrls: [],
            title: "",
            text: "",
            pdfUrls: [],
            pdfFiles: [],
            usedDocs: [],
          },
        },
      };
    }

    default:
      return state;
  }
};

const Dashboard = () => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { data, error, isLoading } = useGetUserQuery();
  const rexdispatch = useDispatch();
  const status = useSelector((state) => state.user.status);
  const user = useSelector((state) => state.user.user);
  const intervalRef = useRef(null);
  const stateRef = useRef(state);
  const [trainLoad, setTrainLoad] = useState(false);
  const [activeOption, setActiveOption] = useState("general"); // menu for account-settings

  useEffect(() => {
    if (isLoading) {
      rexdispatch(setLoading());
    } else if (data) {
      const user = data?.data;
      rexdispatch(setUser(user || {}));
    } else if (error) {
      rexdispatch(setError(error));
    }
  }, [data, error, isLoading, rexdispatch]);

  const handelBotList = async () => {
    /**
     * Fetches and sets the list of bots for the user.
     * Updates local reducer state with fetched bots.
     * Handles loading and error state.
     * @async
     * @function
     */
    dispatch({ type: "SET_LOADING", payload: true });

    const res = await GetUserBots();

    if (!res.error) {
      dispatch({ type: "SET_BOTS", payload: res.data });
    } else {
      dispatch({ type: "SET_ERROR", payload: res.error });
    }
    dispatch({ type: "SET_LOADING", payload: false });
  };

  useEffect(() => {
    if (user?._id) {
      if (user?.penddingJobs && user.penddingJobs.length > 0) {
        const pendingCrawl = user.penddingJobs.filter(
          (task) => task.type === "crawl"
        );
        const pendingProcess = user.penddingJobs.filter(
          (task) => task.type === "process"
        );

        const messages = [];

        if (pendingCrawl.length > 0) {
          pendingCrawl.forEach((ele) => {
            try {
              const data = ele?.metadata
                ? JSON.parse(ele.metadata)
                : { url: "" };
              messages.push({
                type: "crawl",
                domain: data?.url,
                data: [],
                load: true,
                uuid: data?.uuid || "",
                botId: data?.botId,
              });
            } catch (err) {
              console.error("Invalid metadata JSON:", err);
            }
          });

          if (messages && messages.length > 0) {
            console.log("msg", messages);
            messages.map((ele, idx) => {
              // return dispatch({ type: "setUrls", payload: ele });
              const botUrls = state?.botResources?.[ele?.botId]?.urls || [];
              return dispatch({
                type: "UPDATE_BOT_RESOURCE",
                payload: {
                  botId: ele?.botId || "",
                  data: { urls: [...botUrls, ele] },
                },
              });
            });
          }

          dispatch({
            type: "setTask",
            payload: { crawl: pendingCrawl, process: pendingProcess },
          });
        }
      }

      handelBotList();
    }
  }, [user]);

  const handleCreateBot = async (e) => {
    /**
     * Handles bot creation on user request.
     * Sends request to API, handles response, updates UI accordingly.
     * @param {React.FormEvent} e - The form submit event.
     * @async
     */
    e.preventDefault();
    try {
      dispatch({ type: "SET_LOADING", payload: true });

      const res = await CreateNewBot();

      if (res.error) {
        toast.error(res.msg || "Failed to create bot. Please try again.");
      } else {
        toast.success("Bot created successfully!");
        await handelBotList();
        dispatch({ type: "SET_SELECTED_BOT", payload: res.data });
      }
    } catch (err) {
      console.error("Create Bot Error:", err);
      toast.error("An unexpected error occurred.");
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  };

  const handelTrain = async () => {
    /**
     * Handles the training process for the selected bot.
     * Supports multiple data types: text, PDFs (uploaded or linked), and crawl URLs.
     * Splits data into batches and sends to backend.
     * Updates training status in local state.
     *
     * @async
     * @returns {Promise<void>}
     */

    const batchSize = 50;
    const batches = [];
    const bot_id = state?.selectedBot?.botId;

    if (!bot_id) {
      toast.error("No bot selected.");
      return;
    }

    const botData = state?.botResources?.[bot_id] || {
      selectedUrls: [],
      pdfUrls: [],
      pdfFiles: [],
      title: "",
      text: "",
    };

    // Helper to split into batches
    const processLinks = (linksArray, type, subtype) => {
      for (let i = 0; i < linksArray.length; i += batchSize) {
        batches.push({
          type,
          subtype,
          list: linksArray.slice(i, i + batchSize),
          bot_id,
        });
      }
    };

    // 1. Web crawl URLs
    if (botData?.selectedUrls?.length > 0) {
      processLinks(botData.selectedUrls, "html", "web");
    }

    // 2. Linked PDFs
    if (botData?.pdfUrls?.length > 0) {
      processLinks(botData.pdfUrls, "pdf", "web");
    }

    // 3. Uploaded PDFs
    if (botData?.pdfFiles?.length > 0) {
      const dockeys = [];
      for (let file of botData.pdfFiles) {
        const res = await UploadPdf(file);
        if (!res?.error) {
          const uploadOptions = {
            method: "PUT",
            body: file,
          };
          const uploadResponse = await fetch(res.uri, uploadOptions);
          if (uploadResponse.ok) {
            dockeys.push(res.key);
          }
        }
      }
      if (dockeys.length > 0) {
        processLinks(dockeys, "pdf", "s3");
      }
    }

    // 4. Text training
    if (botData?.title && botData?.text) {
      const res = await UploadText(botData.title.trim().replace(/\s+/g, "-"));
      if (!res?.error) {
        const uploadOptions = {
          method: "PUT",
          body: botData.text,
        };
        const uploadResponse = await fetch(res.uri, uploadOptions);
        if (uploadResponse.ok) {
          processLinks([res.key], "text", "s3");
        }
      }
    }

    // Start training
    if (batches.length > 0) {
      const resBatchSize = 5;
      const failures = [];
      setTrainLoad(true);

      for (let i = 0; i < batches.length; i += resBatchSize) {
        const currentBatch = batches.slice(i, i + resBatchSize);
        const results = await Promise.all(
          currentBatch.map((msg) => TrainBotOnData(msg).catch(() => null))
        );

        results.forEach((result, idx) => {
          if (!result) failures.push(i + idx);
        });

        dispatch({
          type: "setTask",
          payload: {
            ...state.task,
            process: {
              [bot_id]: currentBatch,
            },
          },
        });
      }

      // Retry failed batches
      if (failures.length > 0) {
        for (let i = 0; i < failures.length; i++) {
          await TrainBotOnData(batches[failures[i]]);
        }
      }

      toast.success("Your bot is now being trained. This may take a moment.");

      dispatch({
        type: "RESET_BOT_RESOURCE",
        payload: {
          botId: bot_id,
        },
      });
    } else {
      toast.info("No training data found for this bot.");
    }
  };

  const onHandelClick = () => {
    dispatch({ type: "SET_ACTIVE_MENU", payload: "settings" });
    setActiveOption("plans");
  };

  const renderDashboardContent = () => {
    /**
     * Renders dashboard body based on current state:
     * - Selected bot → show `SelectedBot` or `DemoChat`
     * - No bots → show `EmptyList`
     * - Bots available → show `BotList`
     * @returns {React.ReactNode} The dashboard content to render.
     */
    if (state.isSelected) {
      if (state.showDemoChat) {
        return <DemoChat />;
      }
      return (
        <SelectedBot
          dispatch={dispatch}
          state={state}
          handelTrain={handelTrain}
          trainLoad={trainLoad}
        />
      );
    }

    if (state.isEmpty) {
      return (
        <div className=" h-full w-full p-6">
          <EmptyList onCreate={handleCreateBot} dispatch={dispatch} />
        </div>
      );
    }

    return (
      <div className="p-6">
        <BotList
          bots={state.bots}
          onSelect={(bot) => {
            dispatch({ type: "SET_SELECTED_BOT", payload: bot });
          }}
          onCreate={handleCreateBot}
          onHandelClick={onHandelClick}
          dispatch={dispatch}
        />
      </div>
    );
  };

  // Keep stateRef updated
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // // Keep stateRef updated
  // /**
  //  * Polls the server periodically for pending tasks (crawl/process).
  //  * If crawl is complete, fetches data and updates UI.
  //  * Cleanup: Clears interval on component unmount or when no pending tasks.
  //  */
  useEffect(() => {
    const hasPendingTasks =
      (state.task?.crawl?.length ?? 0) > 0 ||
      (state.task?.process && Object.keys(state.task?.process)?.length > 0);

    console.log("hasPendingTasks", !!hasPendingTasks);

    if (!!hasPendingTasks && !intervalRef.current) {
      intervalRef.current = setInterval(async () => {
        try {
          const updatedTasks = await GetListOfPendingTask();

          console.log("updatedTasks", updatedTasks);

          if (updatedTasks?.crawl?.length > 0) {
            for (const ele of updatedTasks.crawl) {
              const update_status = ele?.update_status;
              const metadata = ele?.metadata ? JSON.parse(ele.metadata) : null;

              if (update_status === "complete" && metadata?.uuid) {
                const botId = metadata?.botId;
                const botData = stateRef.current.botResources?.[botId] || {};
                const urls = botData?.urls || [];
                const current = urls.find(
                  (item) => item?.uuid === metadata.uuid
                );
                if (!current) continue;

                const res = await GetCrawlLink(current.domain);

                console.log("auto crawl urls res", res);

                if (res?.error) {
                  toast.error("Error fetching crawl data.");
                  continue;
                }

                if (Array.isArray(res.data) && res.data.length > 0) {
                  const updatedUrls = urls.filter(
                    (item) => item?.uuid !== metadata.uuid
                  );

                  updatedUrls.push({
                    type: "crawl",
                    domain: current.domain,
                    data: res?.data,
                    uuid: current.uuid,
                    load: false,
                  });

                  dispatch({
                    type: "UPDATE_BOT_RESOURCE",
                    payload: {
                      botId,
                      data: {
                        urls: updatedUrls,
                      },
                    },
                  });
                }
              }
            }
          } else {
            dispatch({
              type: "setTask",
              payload: {
                ...(stateRef.current?.task || {}),
                process: updatedTasks?.process,
              },
            });
          }
        } catch (err) {
          console.error("Error polling tasks:", err.message);
        }
      }, 30000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [state.task]);

  useEffect(() => {
    const processTasks = state?.task?.process || {};
    const selectedBotTask = processTasks[state.selectedBot?.botId] || [];

    if (selectedBotTask?.length > 0) {
      setTrainLoad(true);
    } else {
      setTrainLoad(false);
    }
  }, [state?.task, state?.selectedBot]);

  return (
    <>
      {status === "succeeded" && (
        <div className="w-screen h-dvh min-h-dvh bg-white text-black flex flex-col overflow-x-hidden">
          <Navbar dispatch={dispatch} state={state} />
          <div className="flex flex-1 flex-col h-full">
            {state.activeAccountMenus === "dashboard" &&
              renderDashboardContent()}
            {state.activeAccountMenus === "settings" && (
              <AccoundSettings
                activeOption={activeOption}
                setActiveOption={setActiveOption}
              />
            )}
            {state.activeAccountMenus === "usages" && <Usages />}
          </div>
        </div>
      )}
    </>
  );
};

export default Dashboard;
